import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

const formatMoney = (n) =>
    Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * GH confirma desde la notificacion si un pedido de Productos SURA se desconto
 * en la liquidacion del empleado. Es lo unico que GH hace en ese modulo.
 * Si la venta ya se cerro (info.ventaCerrada o soloLectura) queda solo para consulta.
 */
const LiquidacionModal = ({ pedidoId, onClose, soloLectura = false }) => {
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [nota, setNota] = useState('');
    const [rechazando, setRechazando] = useState(false);
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        let vivo = true;
        axiosInstance.get(`/api/ventas-especiales/pedidos/${pedidoId}/liquidacion`)
            .then(r => { if (vivo) setInfo(r.data); })
            .catch(e => { if (vivo) setError(e.response?.data?.error || 'No se pudo cargar el pedido'); });
        return () => { vivo = false; };
    }, [pedidoId]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const responder = async (confirmado) => {
        setEnviando(true);
        setError('');
        try {
            const r = await axiosInstance.post(
                `/api/ventas-especiales/pedidos/${pedidoId}/liquidacion/confirmar`,
                { confirmado, nota }
            );
            setInfo(r.data);
            setRechazando(false);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudo guardar la respuesta');
        } finally {
            setEnviando(false);
        }
    };

    const ventaCerrada = soloLectura || !!info?.ventaCerrada;
    const pendiente = info?.cierreCategoria === 'liquidacion_pendiente' && !ventaCerrada;
    const confirmada = info?.cierreCategoria === 'liquidacion';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label="Descuento por liquidación"
        >
            <div
                className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 px-6 pt-5">
                    <h2 className="text-lg font-semibold text-gray-900">Descuento por liquidación</h2>
                    <button onClick={onClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600 rounded-lg" aria-label="Cerrar">
                        <X size={20} />
                    </button>
                </div>

                <div className="px-6 pb-6 pt-3">
                    {!info && !error && (
                        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
                            <Loader2 size={16} className="animate-spin" /> Cargando pedido
                        </div>
                    )}

                    {info && (
                        <>
                            <p className="text-sm text-gray-600">
                                {info.empleadoNombre} quedó debiendo su pedido de {info.venta}. Costos necesita
                                saber si el monto se le descontó en la liquidación.
                            </p>

                            <p className="mt-5 text-3xl font-semibold text-gray-900 tabular-nums">
                                Bs {formatMoney(info.monto)}
                            </p>

                            <dl className="mt-4 text-sm divide-y divide-gray-100 border-y border-gray-100">
                                <div className="flex justify-between gap-4 py-2">
                                    <dt className="text-gray-500">Ficha</dt>
                                    <dd className="text-gray-900">{info.ficha || 'Sin ficha'}</dd>
                                </div>
                                <div className="flex justify-between gap-4 py-2">
                                    <dt className="text-gray-500">Fecha de egreso</dt>
                                    <dd className="text-gray-900">{info.fechaEgreso || 'Sin registrar'}</dd>
                                </div>
                                {info.motivoEgreso && (
                                    <div className="flex justify-between gap-4 py-2">
                                        <dt className="text-gray-500 shrink-0">Motivo</dt>
                                        <dd className="text-gray-900 text-right break-words min-w-0">{info.motivoEgreso}</dd>
                                    </div>
                                )}
                            </dl>

                            {!pendiente && (
                                <p className={`mt-5 text-sm rounded-xl px-4 py-3 ${confirmada ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-700'}`}>
                                    {confirmada
                                        ? 'Descuento confirmado. Costos ya fue avisado y el pedido quedó como pagado.'
                                        : info.cierreCategoria === 'sin_conciliar'
                                            ? 'Se indicó que no se descontó. Costos ya fue avisado.'
                                            : ventaCerrada && info.cierreCategoria === 'liquidacion_pendiente'
                                                ? 'La venta está cerrada: solo se puede consultar.'
                                                : 'Este pedido no está esperando confirmación.'}
                                    {info.cierreNota && <span className="block mt-1 text-gray-600">Nota: {info.cierreNota}</span>}
                                </p>
                            )}

                            {pendiente && rechazando && (
                                <div className="mt-5">
                                    <label htmlFor="liq-nota" className="block text-sm text-gray-700 mb-1">
                                        ¿Por qué no se descontó?
                                    </label>
                                    <textarea
                                        id="liq-nota"
                                        value={nota}
                                        onChange={(e) => setNota(e.target.value)}
                                        rows={3}
                                        autoFocus
                                        className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

                    {pendiente && (
                        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                            {rechazando ? (
                                <>
                                    <button onClick={() => setRechazando(false)} disabled={enviando} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
                                        Volver
                                    </button>
                                    <button onClick={() => responder(false)} disabled={enviando || !nota.trim()} className="px-4 py-2 text-sm text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50">
                                        Avisar que no se descontó
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setRechazando(true)} disabled={enviando} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
                                        No se descontó
                                    </button>
                                    <button onClick={() => responder(true)} disabled={enviando} className="px-4 py-2 text-sm text-white bg-teal-600 rounded-xl hover:bg-teal-700 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                                        {enviando && <Loader2 size={14} className="animate-spin" />}
                                        Confirmar descuento
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiquidacionModal;
